import { LOGGER_SEVERITY } from './logger-severity';

const CLIENT_LOG_ENDPOINT = '/api/client-log';
const MAX_MESSAGE_LENGTH = 200;
const MAX_BODY_BYTES = 4096;

export type ShippableClientSeverity = typeof LOGGER_SEVERITY.WARN | typeof LOGGER_SEVERITY.ERROR;

export interface ShipClientLogParams {
  severity: ShippableClientSeverity;
  tag: string;
  message: string;
  payload?: object;
}

function truncateMessage(message: string): string {
  return message.length > MAX_MESSAGE_LENGTH ? message.slice(0, MAX_MESSAGE_LENGTH) : message;
}

function buildBody(params: ShipClientLogParams): string {
  return JSON.stringify({
    severity: params.severity,
    tag: params.tag,
    message: truncateMessage(params.message),
    ...(params.payload && Object.keys(params.payload).length > 0
      ? { payload: params.payload }
      : {}),
  });
}

/**
 * Relays warn/error logs from the browser to the server via beacon (or fetch fallback).
 * Fire-and-forget; never throws into application code.
 */
export function shipClientLog(params: ShipClientLogParams): void {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') {
    return;
  }

  if (params.severity !== LOGGER_SEVERITY.WARN && params.severity !== LOGGER_SEVERITY.ERROR) {
    return;
  }

  try {
    const body = buildBody(params);

    if (body.length > MAX_BODY_BYTES) {
      return;
    }

    if (navigator.sendBeacon) {
      navigator.sendBeacon(CLIENT_LOG_ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }

    void fetch(CLIENT_LOG_ENDPOINT, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    });
  } catch {
    // Swallow transport errors — logging must not break the app.
  }
}
